import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Button,
  Card,
  Col,
  FloatingLabel,
  Form,
  Row,
  Container,
  Modal,
  Table
} from "react-bootstrap";
import { PulseLoader } from "react-spinners";
import config from "../../config";
import Layout from "../../components/layouts/Main";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const steps = ["Basic Info", "Raw Materials", "Processing Stages", "Outputs", "Charges"];

const CreateBOM = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validated, setValidated] = useState(false);

  // Function to calculate total output percentage
  const calculateTotalPercentage = () => {
    return form.outputs.reduce((sum, out) => sum + (out.percentage || 0), 0);
  };

  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);

  const [machines, setMachines] = useState([]);

  useEffect(() => {
    axios.get(`${config.API_BASE_URL}/machines`)
      .then(res => setMachines(res.data))
      .catch(console.error);
  }, []);

  const [form, setForm] = useState({
    name: "",
    description: "",
    rawMaterials: [],
    processingStages: [],
    outputs: [],
    charges: [],
  });

  // Modal control states
  const [showRawMaterialModal, setShowRawMaterialModal] = useState(false);
  const [newRawMaterial, setNewRawMaterial] = useState({ materialId: "", quantity: "", unit: "" });

  const [showStageModal, setShowStageModal] = useState(false);
  const [newStage, setNewStage] = useState({ stageName: "", machine: "", capacityPerHour: "", gainLoss: "", capacityUnit: "kg" });

  const [showOutputModal, setShowOutputModal] = useState(false);
  const [newOutput, setNewOutput] = useState({
    productId: "",
    percentage: "",
    projectedOutput: "",
    unit: ""
  });

  const [showChargeModal, setShowChargeModal] = useState(false);
  const [newCharge, setNewCharge] = useState({ description: "", amount: "", type: "fixed" });

  // Fetch units & products
  useEffect(() => {
    axios.get(`${config.API_BASE_URL}/units`).then(res => setUnits(res.data)).catch(console.error);
    axios.get(`${config.API_BASE_URL}/products?populateUnit=true&category=all`)
      .then(res => setProducts(res.data))
      .finally(() => setLoading(false));
  }, []);

  // Handlers
  const addRawMaterial = () => setShowRawMaterialModal(true);
  const saveRawMaterial = () => {
    const product = products.find(p => p._id === newRawMaterial.materialId);
    const unitCode = product?.unitId?.unitCode || "";
    setForm(prev => ({
      ...prev,
      rawMaterials: [...prev.rawMaterials, { ...newRawMaterial, unit: unitCode }],
    }));
    setNewRawMaterial({ materialId: "", quantity: "", unit: "" });
    setShowRawMaterialModal(false);
  };

  const addStage = () => setShowStageModal(true);
  const saveStage = () => {
    setForm(prev => ({
      ...prev,
      processingStages: [...prev.processingStages, newStage],
    }));
    setNewStage({ stageName: "", machine: "", capacityPerHour: "", gainLoss: "", capacityUnit: "kg" });
    setShowStageModal(false);
  };

  const addOutput = () => {
    setNewOutput({ productId: "", percentage: "", projectedOutput: "", unit: "" });
    setShowOutputModal(true);
  };
 
  // charges
  const addCharge = () => setShowChargeModal(true);
  const saveCharge = () => {
    setForm(prev => ({
      ...prev,
      charges: [...prev.charges, newCharge],
    }));
    setNewCharge({ description: "", amount: "", type: "fixed" });
    setShowChargeModal(false);
  };

  // Validation logic
  const isStepValid = () => {
    const { rawMaterials, processingStages, outputs } = form;
    switch (currentStep) {
      case 0:
        // return !!form.name && !!form.description;
        if (!form.name || !form.description) {
          toast.error("Please fill in recipe name and description.");
          return false;
        }
        return true;

      case 1:
        if (rawMaterials.length === 0) {
          toast.error("Please add at least one raw material.");
          return false;
        }
        if (!rawMaterials.every(rm => rm.materialId && rm.quantity > 0 && rm.unit)) {
          toast.error("Some raw material entries are incomplete.");
          return false;
        }
        return true;

      case 2:
        if (processingStages.length === 0) {
          toast.error("Please add at least one processing stage.");
          return false;
        }
        if (!processingStages.every(st => st.stageName && st.capacityPerHour >= 0)) {
          toast.error("Some processing stage entries are incomplete.");
          return false;
        }
        return true;

      case 3:
        if (outputs.length === 0) {
          toast.error("Please add at least one output.");
          return false;
        }
        if (!outputs.every(o => o.productId && o.percentage > 0 && o.projectedOutput >= 0)) {
          toast.error("Some output entries are incomplete.");
          return false;
        }
        const total = outputs.reduce((s, o) => s + (o.percentage || 0), 0);
        if (total < 98 || total > 102) {
          toast.error("Total output percentage must be between 98% and 102%.");
          return false;
        }
        return true;

      // Charges (step 4) is optional, no validation needed
      default:
        return true;
    }
  };

  // Submission
  const handleSubmit = async e => {
    e.preventDefault();
    setValidated(true);
    if (!isStepValid()) return;

    setSaving(true);
    try {
      await axios.post(`${config.API_BASE_URL}/process-definitions`, form);
      toast.success("Bill of Material Created Successfully");
      setCurrentStep(0);
      setForm({ name: "", description: "", rawMaterials: [], processingStages: [], outputs: [] });
      navigate("/boms");
    } catch (err) {
      console.error(err);
      toast.error("Failed to save BOM. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const Stepper = () => (
    <div className="d-flex justify-content-between align-items-center my-5">
      {steps.map((s, i) => (
        <div key={i} className="text-center flex-fill position-relative">
          <div className={`rounded-circle border-2 d-inline-flex align-items-center justify-content-center ${i <= currentStep ? "bg-primary text-white" : "bg-light text-muted"}`} style={{ width: 40, height: 40, marginBottom: 5 }}>{i + 1}</div>
          <div style={{ fontSize: "0.85rem" }} className="d-none d-sm-block">{s}</div>
          {i < steps.length - 1 && <div className="position-absolute top-0" style={{ height: 4, width: "100%", background: i < currentStep ? "#0d6efd" : "#dee2e6", zIndex: -1, left: i === 2 ? '45%' : '48%', transform: 'translateY(19px)' }} />}
        </div>
      ))}
    </div>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <Card>
          <Card.Header><h4>Basic Info</h4></Card.Header>
          <Card.Body>
            <Row className="gy-3">
              <Col md={6}>
                <FloatingLabel label="Recipe Name">
                  <Form.Control name="name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required isInvalid={validated && !form.name} />
                </FloatingLabel>
              </Col>
              <Col md={6}>
                <FloatingLabel label="Description">
                  <Form.Control name="description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required isInvalid={validated && !form.description} />
                </FloatingLabel>
              </Col>
            </Row>
          </Card.Body>
        </Card>;

      case 1:
        return <Card>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h4>Raw Materials</h4>
            <Button size="sm" onClick={addRawMaterial}>+ Add</Button>
          </Card.Header>
          <Card.Body className="pt-0">
            <Row className="gy-4">
              {form.rawMaterials.length === 0 ? (
                <div>No row material added yet.</div>
              ) : (
                <>
                  {form.rawMaterials.map((rm, idx) => {
                    const product = products.find(p => p._id === rm.materialId);
                    const unitCode = rm.unit;

                    return (
                      <Col xs={12} md={6} lg={4} key={idx}>
                        <Card className="h-100 shadow-sm">
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div>
                                <strong>{product?.productName || "(Deleted Product)"}</strong>
                              </div>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() =>
                                  setForm(prev => ({
                                    ...prev,
                                    rawMaterials: prev.rawMaterials.filter((_, i) => i !== idx)
                                  }))
                                }
                              >
                                Remove
                              </Button>
                            </div>
                            <div style={{ fontSize: "0.9rem" }}>
                              <div className="mb-1 text-muted">Quantity: <strong>
                                <span>{rm.quantity}</span>
                                <span className="ms-1 fw-semibold" style={{ fontSize: "0.9rem" }}>
                                  {unitCode}
                                </span>
                              </strong></div>

                            </div>
                          </Card.Body>
                        </Card>
                      </Col>

                    );
                  })}
                </>
              )}
            </Row>
          </Card.Body>

        </Card>;

      case 2:
        return <Card>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h4>Processing Stages</h4>
            <Button size="sm" onClick={addStage}>+ Add</Button>
          </Card.Header>
          <Card.Body>
            {form.processingStages.length === 0 ? (
              <div>No processing stage added yet.</div>
            ) : (
              <>
                {form.processingStages.map((st, idx) => (

                  <Row key={idx} className="mb-3 gy-2 border rounded p-2 text-dark">
                    <Col xs={12} sm={6} xl={3} className="d-flex flex-column align-items-start">
                      <label className="fw-semibold small text-muted opacity-75">Stage Name</label>
                      <div>{st.stageName}</div>
                    </Col>
                    <Col xs={12} sm={6} xl={3} className="d-flex flex-column align-items-start">
                      <label className="fw-semibold small text-muted opacity-75">Machine</label>
                      <div>{
                        machines.find(p => p._id === st.machine)?.name || "Unknown Machine"
                      }</div>
                    </Col>
                    <Col xs={12} sm={6} xl={3} className="d-flex flex-column align-items-start">
                      <label className="fw-semibold small text-muted opacity-75">Capacity per Hour</label>
                      <div>{st.capacityPerHour} {st.capacityUnit}</div>
                    </Col>
                    <Col xs={12} sm={6} xl={3} className="d-flex flex-column align-items-start">
                      <label className="fw-semibold small text-muted opacity-75">Gain/Loss (%)</label>
                      <div className="d-flex justify-content-between gap-2 w-100">
                        <span>{st.gainLoss}%</span>
                        <Button variant="outline-danger" size="sm" onClick={() => {
                          setForm(prev => ({ ...prev, processingStages: prev.processingStages.filter((_, i) => i !== idx) }));
                        }}>Remove</Button>
                      </div>
                    </Col>
                  </Row>
                ))}
              </>
            )}
          </Card.Body>

        </Card>;

      case 3:
        const totalPercentage = calculateTotalPercentage();
        return (
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0">Outputs (Total: {totalPercentage.toFixed(2)}%)</h4>
              <Button size="sm" onClick={addOutput}>+ Add Output</Button>
            </Card.Header>
            <Card.Body>
              <Row className="mt-0">
                {form.outputs.length === 0 ? (
                  <div>No output added yet.</div>
                ) : (
                  <>
                    {form.outputs.map((out, idx) => {
                      const product = products.find(p => p._id === out.productId);
                      return (
                        <Col xs={12} md={6} lg={4} key={idx} className="mb-4">
                          <Card className="h-100 shadow-sm">
                            <Card.Body>
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div>
                                  <strong>{product?.productName || "(Deleted Product)"}</strong>
                                </div>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => {
                                    setForm(prev => ({
                                      ...prev,
                                      outputs: prev.outputs.filter((_, i) => i !== idx)
                                    }));
                                  }}
                                >
                                  Remove
                                </Button>
                              </div>
                              <div style={{ fontSize: "0.9rem" }}>
                                <div className="mb-1 text-muted">Quantity: <strong>{out.percentage} %</strong></div>
                                <div className="text-muted">Projected Output/hr: <strong>{out.projectedOutput}</strong></div>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      );
                    })}
                  </>
                )}
              </Row>
            </Card.Body>
          </Card>
        );

      case 4:
        return (
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4>Charges</h4>
              <Button size="sm" onClick={addCharge}>+ Add Charge</Button>
            </Card.Header>
            <Card.Body>
              {form.charges.length === 0 ? (
                <div>No charges added yet.</div>
              ) : (
                <Table striped bordered hover responsive size="sm">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Description</th>
                      <th>Amount (INR)</th>
                      <th>Type</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.charges.map((charge, idx) => (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        <td>{charge.description}</td>
                        <td>{charge.amount}</td>
                        <td>{charge.type}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() =>
                              setForm(prev => ({
                                ...prev,
                                charges: prev.charges.filter((_, i) => i !== idx)
                              }))
                            }
                          >
                            Remove
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <Layout>
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><Link to="/">Dashboard</Link></li>
          <li className="breadcrumb-item"><Link to="/boms">Recipes</Link></li>
          <li className="breadcrumb-item active">Create BOM</li>
        </ol>
      </nav>
      <Container fluid className="my-5">
        <Row><Col><h2>Create BOM</h2></Col></Row>
        <Stepper />
        {loading ? (
          <div className="text-center my-5"><PulseLoader color="#014b7cbb" size={9} /></div>
        ) : (
          <Form noValidate validated={validated} onSubmit={handleSubmit}>
            {renderStep()}
            <div className="d-flex justify-content-between mt-4">
              {currentStep > 0 && (<Button variant="secondary" onClick={() => setCurrentStep(currentStep - 1)}>Previous</Button>)}
              {currentStep < steps.length - 1 && (
                <>
                  <div></div>
                  <Button variant="primary" onClick={() => {
                    setValidated(true);
                    if (isStepValid()) { setCurrentStep(currentStep + 1); setValidated(false); }
                  }}>
                    Next
                  </Button>
                </>
              )}
              {currentStep === steps.length - 1 && (
                <Button type="submit" disabled={saving}>
                  {saving ? <PulseLoader color="#fff" size={6} /> : "Submit"}
                </Button>
              )}
            </div>
          </Form>
        )}
      </Container>

      {/* Modals */}
      <Modal show={showRawMaterialModal} onHide={() => setShowRawMaterialModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Raw Material</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Material</Form.Label>
            <Form.Select
              value={newRawMaterial.materialId}
              onChange={(e) => {
                const selectedId = e.target.value;
                const selectedProduct = products.find(p => p._id === selectedId);
                const unitCode = selectedProduct?.unitId?.unitCode || "";
                setNewRawMaterial(prev => ({
                  ...prev,
                  materialId: selectedId,
                  unit: unitCode, // Add this so you can use it later too
                }));
              }}
            >
              <option>Select</option>
              {products.map(p => (
                <option key={p._id} value={p._id}>{p.productName}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mt-3 position-relative">
            <Form.Label>Quantity per Batch</Form.Label>
            <div className="position-relative">

              <Form.Control
                type="number"
                value={newRawMaterial.quantity}
                onChange={e =>
                  setNewRawMaterial(prev => ({ ...prev, quantity: Number(e.target.value) }))
                }
              />
              {newRawMaterial.unit && (
                <span
                  className="unit-suffix"
                >
                  {newRawMaterial.unit}
                </span>
              )}
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRawMaterialModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={saveRawMaterial}>Save</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showStageModal} onHide={() => setShowStageModal(false)}>
        <Modal.Header closeButton><Modal.Title>Add Processing Stage</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label>Stage Name</Form.Label>
            <Form.Control value={newStage.stageName} onChange={e => setNewStage(prev => ({ ...prev, stageName: e.target.value }))} />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Machine</Form.Label>
            <Form.Select
              value={newStage.machine}
              onChange={e => setNewStage(prev => ({ ...prev, machine: e.target.value }))}
            >
              <option value="">Select Machine</option>
              {machines.map((machine) => (
                <option key={machine._id} value={machine._id}>{machine.name}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Capacity per Hour</Form.Label>
            <div className="d-flex gap-2">
              <Form.Control
                type="number"
                value={newStage.capacityPerHour}
                onChange={e => setNewStage(prev => ({ ...prev, capacityPerHour: Number(e.target.value) }))}
              />
              <Form.Select
                style={{ maxWidth: "8rem" }}
                value={newStage.capacityUnit}
                onChange={e => setNewStage(prev => ({ ...prev, capacityUnit: e.target.value }))}
              >
                {units.map((u) => (
                  <option key={u._id} value={u.unitCode}>
                    {u.unitName} ({u.unitCode})
                  </option>
                ))}
              </Form.Select>
            </div>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Gain / Loss (%)</Form.Label>
            <Form.Control type="number" value={newStage.gainLoss} onChange={e => setNewStage(prev => ({ ...prev, gainLoss: Number(e.target.value) }))} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStageModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={saveStage}>Save</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showOutputModal} onHide={() => setShowOutputModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Output</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Product</Form.Label>
            <Form.Select
              value={newOutput.productId}
              // onChange={(e) =>
              //   setNewOutput((prev) => ({ ...prev, productId: e.target.value }))
              // }
              onChange={(e) => {
                const selectedOId = e.target.value;
                const selectedOProduct = products.find(p => p._id === selectedOId);
                const unitOCode = selectedOProduct?.unitId?.unitCode || "";
                setNewOutput(prev => ({
                  ...prev,
                  productId: selectedOId,
                  unit: unitOCode,
                }));
              }
              }
            >
              <option value="">Select</option>
              {products.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.productName}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Quantity (%)</Form.Label>
            <div className="position-relative">
            <Form.Control
              type="number"
              value={newOutput.percentage}
              max={102 - calculateTotalPercentage()}
              onChange={(e) =>
                setNewOutput((prev) => ({
                  ...prev,
                  percentage: Number(e.target.value),
                }))
              }
            />
                <span
                  className="unit-suffix"
                >
                  %
                </span>
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Projected Output (per hour)</Form.Label>
            {/* <Form.Control
              type="number"
              value={newOutput.projectedOutput}
              onChange={(e) =>
                setNewOutput((prev) => ({
                  ...prev,
                  projectedOutput: Number(e.target.value),
                }))
              }
            /> */}
            <div className="position-relative">

              <Form.Control
                type="number"
                value={newOutput.projectedOutput}
                onChange={(e) =>
                  setNewOutput((prev) => ({
                    ...prev,
                    projectedOutput: Number(e.target.value),
                  }))
                }
              />
              {newOutput.unit && (
                <span
                  className="unit-suffix"
                >
                  {newOutput.unit}
                </span>
              )}
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowOutputModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              if (newOutput.productId && newOutput.percentage > 0) {
                const proposedTotal = calculateTotalPercentage() + newOutput.percentage;
                if (proposedTotal > 102) {
                  toast.error("Adding this output would exceed the total percentage limit of 102%.");
                  return;
                }
                setForm((prev) => ({
                  ...prev,
                  outputs: [...prev.outputs, newOutput],
                }));
                setNewOutput({ productId: "", percentage: 0, projectedOutput: 0 });
                setShowOutputModal(false);
              } else {
                toast.error("Please fill all required fields");
              }
            }}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showChargeModal} onHide={() => setShowChargeModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Charges</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              value={newCharge.description}
              onChange={e => setNewCharge({ ...newCharge, description: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Amount</Form.Label>
            <Form.Control
              type="number"
              step="0.01" 
              value={newCharge.amount}
              onChange={e => setNewCharge({ ...newCharge, amount: parseFloat(e.target.value) })}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Type</Form.Label>
            <Form.Select
              value={newCharge.type}
              onChange={e => setNewCharge({ ...newCharge, type: e.target.value })}
            >
              <option value="fixed">Fixed</option>
              <option value="per_kg">Per kg</option>
              <option value="per_hour">Per hour</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowChargeModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={saveCharge} >Save</Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
};

export default CreateBOM;
